/**
 * @module lib/commercial/keytar/WallAnalyticsSchema
 */
import logger from "utils/logger";
import isNil from "lodash/isNil";
import { trackGTMEvent } from "lib/audience/gtm-manager";

const debugTag = "[WALL-SCHEMA]";

/**
 * Constant enum for different wall types.
 * Must use a valid wall type for reporting analytics values.
 * - Hard walls are not metered and display immediately.
 * - Metered walls display after a user's meter count expires from viewing articles.
 * - Paywalls prompt registered users to subscribe.
 * - Regwalls prompt anonymous users to register.
 * @see https://confluence.theglobeandmail.com/display/ARC/Paywall+Rules
 * @enum {String}
 */
export const WALL_TYPES = {
  AGEWALL_HARD: "agewall-hard",
  PAYWALL_HARD: "paywall-hard",       // Previously called "redwall"
  PAYWALL_METERED: "paywall-metered", // Previously called "threshwall"
  REGWALL_HARD: "regwall-hard",       // Previously called "regwall" (only used for House Price Data Centre and Newsletters page)
  REGWALL_METERED: "regwall-metered", // Previously called "regwall"
  ADWALL: "adwall"
};

/**
 * Create a wall's meter dataset to send to analytics.
 * @class WallAnalyticsSchema
 */
export default class WallAnalyticsSchema {
  /**
   * Creates an instance of WallAnalyticsSchema.
   * @param {Object} [props={}]
   * @param {String} [props.name]
   * @param {String} [props.type]
   */
  constructor(props = {}) {
    if (!props.name) {
      throw new Error("WallAnalyticsSchema constructor requires a name property");
    }
    if (!props.type) {
      throw new Error("WallAnalyticsSchema constructor requires a type property");
    }
    if (isNil(Object.keys(WALL_TYPES).find(key => WALL_TYPES[key] === props.type))) {
      throw new Error(`WallAnalyticsSchema type property "${props.type}" is invalid`);
    }
    this.name = props.name;
    this.type = props.type;
  }

  /**
   * Get the primary properties for this object.
   * @readonly
   * @returns {Object} of analytics data
   */
  get data() {
    const { name, type } = this;
    return { name, type };
  }

  /**
   * Merge the data param with previous initialized props, and send to analytics layer.
   * @param {*} [data={}]
   * @param {String} [eventName="wallEvent"]
   * @returns {Object} of the event
   */
  sendData(data = {}, eventName = "wallEvent") {
    const wallEventObj = Object.assign(this.data, data);
    if (eventName === "clickEvent") {
      delete wallEventObj.name;
    }
    const ret = {
      event: eventName,
      data: wallEventObj
    };
    logger.info(`${debugTag} sendData:`, ret);
    trackGTMEvent(ret);
    return ret;
  }
};
